/**
 * Topbar navigation model — spec §F3 (the mockup's `const NAV`, mapped onto the §F1
 * routes) with the §I2 role gating. Kept out of Topbar.tsx so that file exports only
 * components (fast refresh) and so tests can assert the model without rendering.
 */
import type { IconName } from '../lib/icons';
import type { Role } from '../lib/state';

export interface NavItem {
  /** The mockup's view id. */
  id: string;
  /** i18n key under `nav.` */
  key: string;
  icon: IconName;
  path: string;
  /** §I2 gating; undefined = every role. */
  roles?: Role[];
  /**
   * True when the item is an action on `/` rather than its own route: "Overview"
   * re-opens the splash (§F4) and "Analysis" opens the toolbox dock (§F6). Neither
   * should light up just because the map route is active.
   */
  action?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { id: 'home', key: 'overview', icon: 'home', path: '/', action: true },
  { id: 'map', key: 'map', icon: 'map', path: '/' },
  { id: 'analysis', key: 'analysis', icon: 'wrench', path: '/', action: true },
  { id: 'dash', key: 'dashboard', icon: 'chart', path: '/dashboard' },
  { id: 'cat', key: 'catalogue', icon: 'db', path: '/catalogue' },
  { id: 'mgr', key: 'manager', icon: 'upload', path: '/manager', roles: ['analyst', 'manager'] },
  { id: 'admin', key: 'admin', icon: 'shield', path: '/admin', roles: ['manager'] },
];

export function visibleNavItems(role: Role): NavItem[] {
  return NAV_ITEMS.filter((n) => !n.roles || n.roles.includes(role));
}

/** Which nav button is highlighted for a given pathname (§F3 "active = gold border"). */
export function activeNavId(pathname: string, role: Role): string | undefined {
  return visibleNavItems(role).find((n) => !n.action && n.path === pathname)?.id;
}
